import CancelIcon from "@mui/icons-material/Cancel";
import UploadIcon from "@mui/icons-material/Upload";
import Avatar from "@mui/material/Avatar";
import Button, { type ButtonProps } from "@mui/material/Button";
import { buttonBaseClasses } from "@mui/material/ButtonBase";
import FormHelperText from "@mui/material/FormHelperText";
import IconButton from "@mui/material/IconButton";
import Stack, { type StackProps } from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useId from "@mui/utils/useId";
import {
  type ChangeEventHandler,
  type DetailedHTMLProps,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useRef,
  useState,
} from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  type RegisterOptions,
  useController,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SvgFileIcon } from "../SvgFileIcon";
import { TooltipHelper } from "../TooltipHelper";
import { VisuallyHiddenInput } from "../VisuallyHiddenInput";

export interface ReactHookFileInputNewProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TFileName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
  label: string;
  name: TName;
  control?: Control<TFieldValues>;
  fileNameName: TFileName;
  required?: boolean;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  inputProps?: DetailedHTMLProps<
    InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
  >;
  onFileReceived: (file: File) => Promise<{
    nameValue: any;
    fileNameValue: any;
  }>;
  onCancelClick?: () => void;
  clearInputAfterChange?: boolean;
  disabled?: boolean;
  tooltip?: ReactNode;
  buttonProps?: ButtonProps;
  stackProps?: StackProps;
  showIcon?: boolean;
  tooltipPosition?: "start" | "end";
  startIcon?: ReactNode;
  showFileName?: boolean;
  showErrorMessage?: boolean;
  dropZone?: boolean;
  loadingIndicator?: ReactNode;
}

export function ReactHookFileInputNew<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TFileName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  label,
  name,
  fileNameName,
  required,
  rules,
  inputProps,
  control,
  disabled,
  tooltip,
  tooltipPosition = "end",
  buttonProps,
  stackProps,
  showIcon = true,
  startIcon,
  showFileName = true,
  showErrorMessage = true,
  onFileReceived,
  dropZone = false,
  loadingIndicator,
  onCancelClick,
}: ReactHookFileInputNewProps<TFieldValues, TName, TFileName>) {
  const { t } = useTranslation("Fixhub");
  const dropText = t("Next:Core:ReactHookFileInputNew.dropText");
  const [isDragging, setIsDragging] = useState(false);

  const {
    field: { onChange: fileInputOnChange, onBlur, ref },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules: {
      required: required
        ? t("Next:Core:FieldRequiredValidationMessage")
        : false,
      ...rules,
    },
  });

  const {
    field: { onChange: fileNameOnChange, value: fileName },
  } = useController({
    control,
    name: fileNameName,
  });
  const errorMessage = error?.message;
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isChanging, setIsChanging] = useState(false);
  const onChange: ChangeEventHandler<HTMLInputElement> = async (e) => {
    if (!e) {
      return;
    }
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }
    setIsChanging(true);
    const { nameValue, fileNameValue } = await onFileReceived(file);
    fileInputOnChange(nameValue);
    fileNameOnChange(fileNameValue);
    setIsChanging(false);
  };
  const onDrop = async (e: React.DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const file = e.dataTransfer?.items?.[0]?.getAsFile();
    if (!file) {
      return;
    }
    setIsChanging(true);
    const { nameValue, fileNameValue } = await onFileReceived(file);
    fileInputOnChange(nameValue);
    fileNameOnChange(fileNameValue);
    setIsChanging(false);
  };

  const actualLabel = required ? `* ${label}` : label;

  const TooltipNode = tooltip ? <TooltipHelper title={tooltip} /> : null;

  return (
    <Stack
      direction="column"
      gap={1}
      alignItems="center"
      maxWidth="100%"
      overflow="hidden"
      {...stackProps}
    >
      <Stack width="100%" direction="row" alignItems="center" gap={1}>
        {tooltipPosition === "start" && TooltipNode}
        <Button
          component="label"
          {...buttonProps}
          sx={{
            flex: 1,
            height: dropZone ? "150px" : "auto",
            border: (theme) => {
              if (!dropZone) {
                return undefined;
              }
              let color = isDragging
                ? theme.palette.primary.main
                : theme.palette.primary.lighter;
              if (error) {
                color = theme.palette.error.light;
              }
              return `1px dashed ${color}`;
            },
            [`&.${buttonBaseClasses.disabled}`]: {
              border: (theme) =>
                dropZone ? `2px dashed ${theme.palette.divider}` : undefined,
            },
            ...buttonProps?.sx,
          }}
          onDragOver={
            dropZone
              ? (e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  setIsDragging(true);
                }
              : undefined
          }
          onDragLeave={
            dropZone
              ? (e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  setIsDragging(false);
                }
              : undefined
          }
          onDrop={
            dropZone
              ? (e: React.DragEvent<HTMLButtonElement>) => {
                  e.preventDefault();
                  setIsDragging(false);
                  onDrop(e);
                }
              : undefined
          }
          onKeyUp={(e: KeyboardEvent) => {
            if (
              e !== null &&
              e.key === "Enter" &&
              e.currentTarget !== null &&
              "click" in e.currentTarget &&
              typeof e.currentTarget.click === "function"
            ) {
              e.currentTarget.click();
            }
          }}
          disabled={disabled}
          variant="outlined"
          color={error ? "error" : "primary"}
          loadingPosition="start"
          loading={isChanging}
          loadingIndicator={loadingIndicator}
          startIcon={startIcon ?? <UploadIcon />}
        >
          {isDragging && dropZone ? dropText : actualLabel}
          <VisuallyHiddenInput
            ref={ref}
            type="file"
            name={name}
            tabIndex={-1}
            id={inputId}
            onBlur={onBlur}
            onChange={onChange}
            {...inputProps}
          />
        </Button>
        {tooltipPosition === "end" && TooltipNode}
      </Stack>
      {showErrorMessage && errorMessage && (
        <FormHelperText sx={{ color: "error.main" }}>
          {errorMessage}
        </FormHelperText>
      )}
      {showFileName && fileName && (
        <Stack direction="row" gap={1} alignItems="center">
          {showIcon && (
            <Avatar
              sx={{
                bgcolor: "divider",
                color: (theme) => theme.palette.text.secondary,
              }}
            >
              <SvgFileIcon fileName={fileName} />
            </Avatar>
          )}
          <Typography sx={{ wordBreak: "break-word" }} variant="caption">
            {fileName}
          </Typography>
          <IconButton
            size="small"
            onClick={() => {
              if (inputRef.current !== null) {
                inputRef.current.value = "";
              }
              fileNameOnChange("");
              fileInputOnChange("");
              onCancelClick?.();
            }}
          >
            <CancelIcon />
          </IconButton>
        </Stack>
      )}
    </Stack>
  );
}
